import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconCalendarEvent,
  IconCheck,
  IconClock,
  IconFileText,
  IconSearch,
  IconUser,
  IconX,
} from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import { Lead } from '../../../entities/lead/model/types';
import { pb } from '../../../shared/api/pocketbase';
import { DateInput } from '../../../shared/ui/DateInput';
import { FormField } from '../../../shared/ui/FormField';
import { TextArea } from '../../../shared/ui/Textarea';
import { TextInput } from '../../../shared/ui/TextInput';
import { TimePicker } from '../../../shared/ui/TimePicker';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';

interface LeadOption {
  id: string;
  title: string;
  num?: number | null;
}

export interface CreateForm {
  name: string;
  tasksId: string;
  tasksTitle: string;
  ownerId: string;
  ownerName: string;
  planDate: string;
}

export const EMPTY_FORM: CreateForm = {
  name: '',
  tasksId: '',
  tasksTitle: '',
  ownerId: '',
  ownerName: '',
  planDate: '',
};

const LeadSearchInput: React.FC<{
  value: string;
  selectedTitle: string;
  onSelect: (id: string, title: string) => void;
  onClear: () => void;
}> = ({ value, selectedTitle, onSelect, onClear }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LeadOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number>(0);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsSearching(true);
    try {
      const safe = term.replace(/"/g, '');
      const safeLower = safe.toLowerCase();
      const safeTitle = safeLower
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      const titleVariants = [...new Set([safe, safeLower, safeTitle])];
      const titlePart = titleVariants.map(v => `title ~ "${v}"`).join(' || ');
      const list = await pb.collection('tasks').getList<Lead>(1, 10, {
        filter: `${titlePart} || id ~ "${safe}"`,
        sort: '-created',
        fields: 'id,title,num',
      });

      setResults(list.items.map(l => ({ id: l.id, title: l.title ?? '', num: l.num })));
      setIsOpen(true);
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => search(val), 300);
  };

  const handleSelect = (opt: LeadOption) => {
    onSelect(opt.id, opt.title || `#${opt.id.slice(-4)}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  if (value) {
    return (
      <div className="lead-search-selected">
        <IconFileText size={13} stroke={2} />
        <span>{selectedTitle}</span>
        <button type="button" className="lead-search-clear" onClick={onClear}>
          <IconX size={12} stroke={2.5} />
        </button>
      </div>
    );
  }

  return (
    <div className="lead-search-wrap" ref={wrapRef}>
      <div className="lead-search-input-wrap">
        <IconSearch size={14} stroke={2} />
        <input
          className="inf-input lead-search-input"
          name="linked-task-search"
          value={query}
          onChange={handleChange}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder="Поиск по названию или ID"
          autoComplete="off"
        />
        {isSearching && <div className="lead-search-spinner" />}
      </div>
      {isOpen && results.length > 0 && (
        <div className="lead-search-dropdown">
          {results.map(opt => (
            <div
              key={opt.id}
              className="lead-search-option"
              onMouseDown={() => handleSelect(opt)}
            >
              <span className="lead-search-opt-title">{opt.title || 'Без названия'}</span>
              <span className="lead-search-opt-id">#{opt.id.slice(-4)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface CreateTaskModalProps {
  open: boolean;
  initialForm?: CreateForm;
  isSaving: boolean;
  onClose: () => void;
  onCreate: (values: CreateForm) => Promise<void>;
  showTaskSelect?: boolean;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  open,
  initialForm = EMPTY_FORM,
  isSaving,
  onClose,
  onCreate,
  showTaskSelect = true,
}) => {
  const [allDay, setAllDay] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
    register,
  } = useForm<CreateForm>({
    defaultValues: initialForm,
  });
  const planDate = watch('planDate');
  const ownerName = watch('ownerName');
  const tasksTitle = watch('tasksTitle');

  useEffect(() => {
    if (!open) return;
    reset(initialForm);
    setAllDay(initialForm.planDate.endsWith('T23:59'));
  }, [initialForm, open, reset]);

  const handleToggleAllDay = () => {
    const next = !allDay;
    setAllDay(next);
    if (next) {
      const date = planDate ? planDate.slice(0, 10) : '';
      setValue('planDate', date ? `${date}T23:59` : '', { shouldDirty: true, shouldValidate: true });
    }
  };

  const todayStr = dayjs().format('YYYY-MM-DD');
  const selectedDate = planDate ? planDate.slice(0, 10) : '';
  const minTime = selectedDate === todayStr ? dayjs().format('HH:mm') : undefined;

  if (!open) return null;

  const handleCreate = handleSubmit(async values => {
    await onCreate(values);
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="cm cm-create cm-create-task" onClick={e => e.stopPropagation()}>
        <div className="cm-top">
          <div>
            <div className="cm-ti">Новое задание</div>
            <div className="cm-sub">Заполните все поля для создания</div>
          </div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={18} stroke={2.2} />
          </button>
        </div>
        <form className="cm-create-body cm-create-body-no-scroll" onSubmit={handleCreate}>
          <div className="tcf-fields">
            <FormField
              className="tcf-field"
              label={(
                <>
                  <IconFileText size={13} stroke={2} />
                  Название задания
                </>
              )}
              labelClassName="tcf-label"
              error={errors.name?.message}
            >
              {showTaskSelect ? (
                <TextInput
                  className="inf-input"
                  name="task-name"
                  placeholder="Что нужно сделать?"
                  autoFocus
                  {...register('name', {
                    required: 'Укажите название задания',
                    validate: value => value.trim() !== '' || 'Укажите название задания',
                  })}
                />
              ) : (
                <TextArea
                  className="inf-input asgn-modal-name-area"
                  name="task-name"
                  placeholder="Что нужно сделать?"
                  autoFocus
                  {...register('name', {
                    required: 'Укажите название задания',
                    validate: value => value.trim() !== '' || 'Укажите название задания',
                  })}
                />
              )}
            </FormField>
            {showTaskSelect && (
              <FormField
                className="tcf-field"
                label={(
                  <>
                    <IconCalendarEvent size={13} stroke={2} />
                    Связанная задача
                  </>
                )}
                labelClassName="tcf-label"
                error={errors.tasksId?.message}
              >
                <Controller
                  control={control}
                  name="tasksId"
                  rules={showTaskSelect ? { required: 'Выберите связанную задачу' } : undefined}
                  render={({ field }) => (
                    <LeadSearchInput
                      value={field.value}
                      selectedTitle={tasksTitle}
                      onSelect={(id, selectedTitle) => {
                        field.onChange(id);
                        setValue('tasksTitle', selectedTitle, { shouldDirty: true });
                      }}
                      onClear={() => {
                        field.onChange('');
                        setValue('tasksTitle', '', { shouldDirty: true });
                      }}
                    />
                  )}
                />
              </FormField>
            )}
            <div className="tcf-row">
              <FormField
                className="tcf-field tcf-field-grow"
                label={(
                  <>
                    <IconUser size={13} stroke={2} />
                    Исполнитель
                  </>
                )}
                labelClassName="tcf-label"
                error={errors.ownerId?.message}
              >
                <Controller
                  control={control}
                  name="ownerId"
                  rules={{ required: 'Выберите исполнителя' }}
                  render={({ field }) => (
                    <UserSearchInput
                      name="task-owner"
                      value={field.value}
                      selectedName={ownerName}
                      onSelect={(id, name) => {
                        field.onChange(id);
                        setValue('ownerName', name, { shouldDirty: true });
                      }}
                      onClear={() => {
                        field.onChange('');
                        setValue('ownerName', '', { shouldDirty: true });
                      }}
                    />
                  )}
                />
              </FormField>
              <div className="tcf-field tcf-field-date">
                <input
                  type="hidden"
                  {...register('planDate', {
                    required: 'Укажите срок',
                    validate: v => dayjs(v).isAfter(dayjs()) || 'Нельзя указать прошедшее время',
                  })}
                />
                <div className="asgn-field-header">
                  <label className="tcf-label">
                    <IconClock size={13} stroke={2} />
                    Срок
                  </label>
                  <button
                    className={`asgn-allday-toggle${allDay ? ' active' : ''}`}
                    type="button"
                    onClick={handleToggleAllDay}
                  >
                    <div className="asgn-allday-cb">
                      {allDay && <IconCheck size={9} stroke={3} />}
                    </div>
                    Весь день
                  </button>
                </div>
                <div className="tcf-datetime">
                  <div className="tcf-datetime-part">
                    <IconCalendarEvent size={13} stroke={2} className="tcf-datetime-icon" />
                    <DateInput
                      className="inf-input"
                      name="task-plan-date"
                      min={todayStr}
                      value={planDate ? planDate.slice(0, 10) : ''}
                      onChange={e => {
                        const time = allDay ? '23:59' : (planDate ? planDate.slice(11) : '09:00');
                        setValue('planDate', e.target.value ? `${e.target.value}T${time}` : '', {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </div>
                  {!allDay && (
                    <div className="tcf-datetime-part">
                      <IconClock size={13} stroke={2} className="tcf-datetime-icon" />
                      <TimePicker
                        className="inf-input"
                        name="task-plan-time"
                        min={minTime}
                        value={planDate ? planDate.slice(11) : ''}
                        onChange={e => {
                          const date = planDate
                            ? planDate.slice(0, 10)
                            : dayjs().format('YYYY-MM-DD');
                          setValue('planDate', `${date}T${e.target.value}`, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
                {errors.planDate?.message ? <div className="form-field-error">{errors.planDate.message}</div> : null}
              </div>
            </div>
          </div>
          <div className="cm-r-actions" style={{ marginTop: 8, paddingTop: 0 }}>
            <button
              className="btn-save"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Создание...' : 'Создать задание'}
            </button>
            <button className="btn-can" type="button" onClick={onClose}>
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export { CreateTaskModal };
